import { useNavigate } from '@tanstack/react-router';
import { memo, useEffect, useRef } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useAnalyticsFilters } from '../filters';
import { usePrefetchAnalytics } from '../../../hooks/analytics/usePrefetchAnalytics';
import { ANALYTICS_CATEGORIES } from '../config';
import {
  SessionPerformancePreview,
  UtilizationPreview,
  WellnessPreview,
  SystemPerformancePreview,
  ComingSoonPreview,
} from './CategoryPreviews';
import { AnalyticsErrorBoundary } from '../shared/AnalyticsErrorBoundary';
import AnalyticsFilters from '../AnalyticsFilters';
import AnalyticsExportButton from '../export/AnalyticsExportButton';
import { cssVars, categoryAccent } from '../shared/theme';

function OverviewSkeleton() {
  const isMobile = useIsMobile();
  return (
    <div
      className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}
      aria-busy="true"
      aria-label="Loading analytics overview"
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="h-full overflow-hidden rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] motion-safe:animate-pulse"
          style={{ borderBottomWidth: 3, borderBottomColor: 'var(--anl-border)' }}
        >
          <div className="border-b border-[var(--anl-border)] bg-[var(--anl-surface)] px-4 py-3">
            <div className="flex items-center space-x-2">
              <div className="bg-[var(--anl-border)]/40 h-7 w-7 rounded-lg" />
              <div className="bg-[var(--anl-border)]/40 h-4 w-32 rounded" />
            </div>
            <div className="bg-[var(--anl-border)]/30 mt-2 h-3 w-48 rounded" />
          </div>
          <div className="h-64 p-4">
            <div className="grid h-full grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <div
                  key={j}
                  className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-metric-bg)] p-3"
                >
                  <div className="bg-[var(--anl-border)]/40 mb-2 h-7 w-12 rounded" />
                  <div className="bg-[var(--anl-border)]/30 h-3 w-20 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const PREVIEW_COMPONENTS = {
  'session-performance': SessionPerformancePreview,
  utilization: UtilizationPreview,
  wellness: WellnessPreview,
  'system-performance': SystemPerformancePreview,
} as const;

interface CategoryCardProps {
  category: (typeof ANALYTICS_CATEGORIES)[0];
  filters: BaseAnalyticsFilter;
  onCardClick: (route: string, isImplemented: boolean) => void;
}

const CategoryCard = memo(({ category, filters, onCardClick }: CategoryCardProps) => {
  const IconComponent = category.icon;
  const accent = categoryAccent[category.id] || categoryAccent['session-performance'];
  const PreviewComponent =
    PREVIEW_COMPONENTS[category.id as keyof typeof PREVIEW_COMPONENTS] ||
    (() => (
      <ComingSoonPreview icon={category.icon as any} title={category.title} accent={accent} />
    ));

  return (
    <div
      className={`h-full overflow-hidden rounded-lg border transition-all duration-200 ${
        category.isImplemented
          ? 'cursor-pointer hover:scale-[1.02] hover:shadow-md'
          : 'cursor-not-allowed opacity-75'
      } border-[var(--anl-border)] bg-[var(--anl-surface)]`}
      style={{ borderBottomWidth: 3, borderBottomColor: accent }}
      onClick={() => onCardClick(category.route, category.isImplemented)}
    >
      <div className="border-b border-[var(--anl-border)] bg-[var(--anl-surface)] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ backgroundColor: accent }}
            >
              <IconComponent className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[var(--anl-text)]">{category.title}</h3>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-1 text-xs text-[var(--anl-text-muted)]">{category.description}</p>
      </div>

      <div className="h-64">
        <PreviewComponent filters={filters} accent={accent} />
      </div>
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';

export default function AnalyticsOverview() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { filters, updateFilters, clearFilters, isInitialized } = useAnalyticsFilters();
  const hasInitializedRef = useRef(false);

  usePrefetchAnalytics(filters);

  useEffect(() => {
    if (!hasInitializedRef.current && filters.granularity === 'day') {
      hasInitializedRef.current = true;
      updateFilters({ granularity: 'month', startDate: undefined, endDate: undefined });
    }
  }, [filters.granularity, updateFilters]);

  const handleCardClick = (route: string, isImplemented: boolean) => {
    if (isImplemented) {
      navigate({ to: route });
    }
  };

  return (
    <AnalyticsErrorBoundary>
      <div className="flex min-h-full flex-col" style={cssVars}>
        <div className="relative z-10 mt-4 flex-shrink-0">
          <AnalyticsFilters
            filters={filters}
            onFiltersChange={updateFilters}
            onClearFilters={clearFilters}
            actions={<AnalyticsExportButton filters={filters} />}
          />
        </div>

        <div className={`flex-1 ${isMobile ? 'p-4' : 'p-8'}`}>
          {!isInitialized ? (
            <OverviewSkeleton />
          ) : (
            <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'grid-cols-2 gap-6'}`}>
              {ANALYTICS_CATEGORIES.map((category: (typeof ANALYTICS_CATEGORIES)[number]) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  filters={filters}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AnalyticsErrorBoundary>
  );
}
